import React, { useState, useEffect } from 'react';
import { Heart, Plus, Edit3, Trash2, Save, X, Check, AlertCircle, Lightbulb } from 'lucide-react';
import { AdminLayout } from '../../components/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { adminAPI } from '../../lib/api';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../../components/ui/alert-dialog';

const iconOptions = ['Heart', 'Activity', 'Droplet', 'Apple'];

const sexOptions = [
  { value: 'female', label: 'Female' },
  { value: 'male', label: 'Male' },
  { value: 'other', label: 'Other' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
];

export default function AdminHealthGoalsPage() {
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    id: '',
    label: '',
    description: '',
    icon: 'Heart',
    applicable_to: ['female', 'male', 'other', 'prefer_not_to_say'],
    active: true,
    is_suggested: false,
  });

  useEffect(() => {
    loadGoals();
  }, []);

  const loadGoals = async () => {
    try {
      const response = await adminAPI.getHealthGoals();
      setGoals(response.data.goals || []);
    } catch (error) {
      console.error('Failed to load health goals:', error);
      toast.error('Failed to load health goals');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      id: '',
      label: '',
      description: '',
      icon: 'Heart',
      applicable_to: ['female', 'male', 'other', 'prefer_not_to_say'],
      active: true,
      is_suggested: false,
    });
  };

  const handleAddGoal = async (e) => {
    e.preventDefault();
    if (!formData.id || !formData.label) {
      toast.error('Please fill in required fields');
      return;
    }

    setSaving(true);
    try {
      await adminAPI.createHealthGoal(formData);
      toast.success('Health goal created');
      setShowAddDialog(false);
      resetForm();
      loadGoals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create health goal');
    } finally {
      setSaving(false);
    }
  };

  const handleEditGoal = (goal) => {
    setEditingGoal(goal);
    setFormData({
      id: goal.id,
      label: goal.label,
      description: goal.description || '',
      icon: goal.icon || 'Heart',
      applicable_to: goal.applicable_to || ['female', 'male', 'other', 'prefer_not_to_say'],
      active: goal.active !== false,
      is_suggested: goal.is_suggested || false,
    });
  };

  const handleSaveGoal = async () => {
    if (!editingGoal) return;
    
    setSaving(true);
    try {
      const { id, ...updateData } = formData;
      await adminAPI.updateHealthGoal(editingGoal.id, updateData);
      toast.success('Health goal updated');
      setEditingGoal(null);
      resetForm();
      loadGoals();
    } catch (error) {
      toast.error('Failed to update health goal');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteGoal = async (goalId) => {
    try {
      await adminAPI.deleteHealthGoal(goalId);
      toast.success('Health goal deleted');
      loadGoals();
    } catch (error) {
      toast.error('Failed to delete health goal');
    }
  };

  const handleApproveSuggested = async (goal) => {
    try {
      await adminAPI.updateHealthGoal(goal.id, { is_suggested: false, active: true });
      toast.success('Suggested goal approved and activated');
      loadGoals();
    } catch (error) {
      toast.error('Failed to approve goal');
    }
  };

  const toggleApplicableTo = (sex) => {
    setFormData(prev => ({
      ...prev,
      applicable_to: prev.applicable_to.includes(sex)
        ? prev.applicable_to.filter(s => s !== sex)
        : [...prev.applicable_to, sex]
    }));
  };

  // Separate active, inactive, and suggested goals
  const activeGoals = goals.filter(g => g.active && !g.is_suggested);
  const inactiveGoals = goals.filter(g => !g.active && !g.is_suggested);
  const suggestedGoals = goals.filter(g => g.is_suggested);

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="spinner" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-8" data-testid="admin-health-goals-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl text-[#4A2C2A]">Health Goals</h1>
            <p className="text-[#4A2C2A]/60 mt-1">
              Manage the health goals shown during user onboarding
            </p>
          </div>
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button className="bg-[#4A2C2A] text-white hover:bg-[#382120] rounded-xl" data-testid="add-goal-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Health Goal
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="font-heading text-xl">Add Health Goal</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddGoal} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>ID (unique) *</Label>
                    <Input
                      value={formData.id}
                      onChange={(e) => setFormData(prev => ({ ...prev, id: e.target.value.toLowerCase().replace(/\s+/g, '_') }))}
                      placeholder="e.g., sleep_quality"
                      required
                      data-testid="goal-id-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Label *</Label>
                    <Input
                      value={formData.label}
                      onChange={(e) => setFormData(prev => ({ ...prev, label: e.target.value }))}
                      placeholder="e.g., Sleep Quality"
                      required
                      data-testid="goal-label-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Brief description shown to users"
                    rows={2}
                    data-testid="goal-description-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Icon</Label>
                  <div className="flex gap-2">
                    {iconOptions.map(icon => (
                      <button
                        key={icon}
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, icon }))}
                        className={`px-3 py-2 rounded-lg border ${
                          formData.icon === icon 
                            ? 'border-[#E8A095] bg-[#E8A095]/10' 
                            : 'border-[#E8A095]/20 hover:border-[#E8A095]/40'
                        }`}
                      >
                        {icon}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Applicable To</Label>
                  <div className="flex flex-wrap gap-2">
                    {sexOptions.map(option => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => toggleApplicableTo(option.value)}
                        className={`px-3 py-1.5 rounded-full text-sm ${
                          formData.applicable_to.includes(option.value)
                            ? 'bg-[#E8A095] text-white'
                            : 'bg-[#E8A095]/10 text-[#4A2C2A]'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-[#4A2C2A]/50">Select which users can see this goal based on their sex</p>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="active"
                    checked={formData.active}
                    onChange={(e) => setFormData(prev => ({ ...prev, active: e.target.checked }))}
                    className="rounded border-[#E8A095]/30"
                  />
                  <Label htmlFor="active" className="cursor-pointer">Active (visible in onboarding)</Label>
                </div>
                <Button
                  type="submit"
                  disabled={saving}
                  className="w-full bg-[#4A2C2A] text-white hover:bg-[#382120]"
                  data-testid="save-goal-btn"
                >
                  {saving ? 'Creating...' : 'Create Goal'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Suggested Goals (User Submissions) */}
        {suggestedGoals.length > 0 && (
          <div className="soft-card p-6 border-l-4 border-[#E8C495]">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-[#E8C495]/20 flex items-center justify-center">
                <Lightbulb className="w-5 h-5 text-[#E8C495]" />
              </div>
              <div>
                <h2 className="font-heading text-lg text-[#4A2C2A]">User Suggestions ({suggestedGoals.length})</h2>
                <p className="text-sm text-[#4A2C2A]/60">Goals submitted by users via the "Other" option</p>
              </div>
            </div>
            <div className="space-y-3">
              {suggestedGoals.map(goal => (
                <div key={goal.id} className="flex items-center justify-between p-4 bg-[#FDFBF7] rounded-xl">
                  <div>
                    <p className="font-medium text-[#4A2C2A]">{goal.label}</p>
                    <p className="text-sm text-[#4A2C2A]/60">{goal.description}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      onClick={() => handleApproveSuggested(goal)}
                      className="bg-[#8DA399] text-white hover:bg-[#7A9188]"
                      data-testid={`approve-goal-${goal.id}`}
                    >
                      <Check className="w-3 h-3 mr-1" /> Approve
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete Suggestion?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This will permanently delete this user suggestion.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction 
                            onClick={() => handleDeleteGoal(goal.id)}
                            className="bg-red-500 hover:bg-red-600"
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Active Goals */}
        <div className="soft-card p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-[#8DA399]/20 flex items-center justify-center">
              <Heart className="w-5 h-5 text-[#8DA399]" />
            </div>
            <div>
              <h2 className="font-heading text-lg text-[#4A2C2A]">Active Goals ({activeGoals.length})</h2>
              <p className="text-sm text-[#4A2C2A]/60">Visible to users during onboarding</p>
            </div>
          </div>
          
          {activeGoals.length > 0 ? (
            <div className="space-y-3">
              {activeGoals.map(goal => (
                <div key={goal.id} className="flex items-center justify-between p-4 bg-[#FDFBF7] rounded-xl">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-xl bg-[#E8A095]/20 flex items-center justify-center">
                      <Heart className="w-5 h-5 text-[#E8A095]" />
                    </div>
                    <div>
                      <p className="font-medium text-[#4A2C2A]">{goal.label}</p>
                      <p className="text-sm text-[#4A2C2A]/60">{goal.description}</p>
                      <div className="flex gap-1 mt-1">
                        {goal.applicable_to?.map(sex => (
                          <span key={sex} className="px-2 py-0.5 bg-[#E8A095]/10 text-[#4A2C2A]/70 rounded text-xs capitalize">
                            {sex.replace(/_/g, ' ')}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Dialog open={editingGoal?.id === goal.id} onOpenChange={(open) => !open && setEditingGoal(null)}>
                      <DialogTrigger asChild>
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => handleEditGoal(goal)}
                          data-testid={`edit-goal-${goal.id}`}
                        >
                          <Edit3 className="w-4 h-4" />
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle className="font-heading text-xl">Edit Health Goal</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4">
                          <div className="space-y-2">
                            <Label>Label</Label>
                            <Input
                              value={formData.label}
                              onChange={(e) => setFormData(prev => ({ ...prev, label: e.target.value }))}
                              data-testid="edit-goal-label"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Description</Label>
                            <Textarea
                              value={formData.description}
                              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                              rows={2}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Applicable To</Label>
                            <div className="flex flex-wrap gap-2">
                              {sexOptions.map(option => (
                                <button
                                  key={option.value}
                                  type="button"
                                  onClick={() => toggleApplicableTo(option.value)}
                                  className={`px-3 py-1.5 rounded-full text-sm ${
                                    formData.applicable_to.includes(option.value)
                                      ? 'bg-[#E8A095] text-white'
                                      : 'bg-[#E8A095]/10 text-[#4A2C2A]'
                                  }`}
                                >
                                  {option.label}
                                </button>
                              ))}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              id="edit-active"
                              checked={formData.active}
                              onChange={(e) => setFormData(prev => ({ ...prev, active: e.target.checked }))}
                              className="rounded border-[#E8A095]/30"
                            />
                            <Label htmlFor="edit-active" className="cursor-pointer">Active</Label>
                          </div>
                          <div className="flex justify-end gap-2">
                            <Button variant="outline" onClick={() => setEditingGoal(null)}>
                              Cancel
                            </Button>
                            <Button 
                              onClick={handleSaveGoal} 
                              disabled={saving}
                              className="bg-[#4A2C2A] text-white hover:bg-[#382120]"
                            >
                              {saving ? 'Saving...' : 'Save Changes'}
                            </Button>
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-goal-${goal.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete Health Goal?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This will permanently delete "{goal.label}". Users who selected this goal will retain it in their profile.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction 
                            onClick={() => handleDeleteGoal(goal.id)}
                            className="bg-red-500 hover:bg-red-600"
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-[#4A2C2A]/50 text-center py-6">No active goals. Add some to enable onboarding.</p>
          )}
        </div>

        {/* Inactive Goals */}
        {inactiveGoals.length > 0 && (
          <div className="soft-card p-6 opacity-75">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-[#4A2C2A]/10 flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-[#4A2C2A]/50" />
              </div>
              <div>
                <h2 className="font-heading text-lg text-[#4A2C2A]">Inactive Goals ({inactiveGoals.length})</h2>
                <p className="text-sm text-[#4A2C2A]/60">Not visible to users</p>
              </div>
            </div>
            <div className="space-y-3">
              {inactiveGoals.map(goal => (
                <div key={goal.id} className="flex items-center justify-between p-4 bg-[#FDFBF7] rounded-xl">
                  <div>
                    <p className="font-medium text-[#4A2C2A]/70">{goal.label}</p>
                    <p className="text-sm text-[#4A2C2A]/50">{goal.description}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEditGoal(goal)}
                    >
                      <Edit3 className="w-3 h-3 mr-1" /> Edit
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete Health Goal?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This will permanently delete "{goal.label}".
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction 
                            onClick={() => handleDeleteGoal(goal.id)}
                            className="bg-red-500 hover:bg-red-600"
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
